import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Loader2, Send, CheckCircle2, Clock, MessageSquareText,
    ShieldCheck, AlertTriangle, XCircle, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { stringifyDetail } from '../lib/utils';
import { parseUtc } from '../lib/datetime';

const API_BASE = window.location.origin;

const formatTimestamp = (iso) => {
    try {
        const d = parseUtc(iso);
        const now = new Date();
        const diffMs = now - d;
        const diffH = diffMs / (1000 * 60 * 60);
        if (diffH < 1) {
            const mins = Math.floor(diffMs / 60000);
            return mins < 2 ? 'Just now' : `${mins}m ago`;
        }
        if (diffH < 24) return `${Math.floor(diffH)}h ago`;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
};

const formatTimeRemaining = (iso) => {
    try {
        const exp = parseUtc(iso);
        const now = new Date();
        const hoursLeft = Math.max(0, (exp - now) / (1000 * 60 * 60));
        if (hoursLeft <= 0) return 'Expired';
        if (hoursLeft < 1) return `${Math.floor(hoursLeft * 60)}m left`;
        if (hoursLeft < 24) return `${Math.floor(hoursLeft)}h left`;
        return `${Math.floor(hoursLeft / 24)}d ${Math.floor(hoursLeft % 24)}h left`;
    } catch { return ''; }
};

const STATUS_CONFIG = {
    active: { label: 'Active', color: 'bg-emerald-500', textColor: 'text-emerald-700', icon: MessageSquareText },
    inactive: { label: 'Inactive', color: 'bg-amber-500', textColor: 'text-amber-700', icon: Clock },
    resolved: { label: 'Resolved', color: 'bg-blue-500', textColor: 'text-blue-700', icon: CheckCircle2 },
    stopped: { label: 'Stopped', color: 'bg-slate-500', textColor: 'text-slate-600', icon: XCircle },
    expired: { label: 'Expired', color: 'bg-red-500', textColor: 'text-red-700', icon: AlertTriangle },
};

export default function RespondNow() {
    const { t } = useTranslation();
    const { feedbackId } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [phase, setPhase] = useState('init'); // init | creating | active | error | ended
    const [session, setSession] = useState(null);
    const [error, setError] = useState('');
    const [messageText, setMessageText] = useState('');
    const [sending, setSending] = useState(false);
    const [resolving, setResolving] = useState(false);
    const messagesEndRef = useRef(null);
    const pollRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Step 1: Auto-create the session
    useEffect(() => {
        if (!token || !feedbackId) {
            setError('Invalid link — missing parameters.');
            setPhase('error');
            return;
        }

        const createSession = async () => {
            setPhase('creating');
            try {
                const res = await fetch(
                    `${API_BASE}/api/recovery/respond-now/${feedbackId}?token=${encodeURIComponent(token)}`,
                    { method: 'POST' }
                );
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    throw new Error(stringifyDetail(data.detail) || `Error ${res.status}`);
                }
                const data = await res.json();
                // Now fetch the full session
                await loadSession();
            } catch (e) {
                setError(e.message);
                setPhase('error');
            }
        };

        createSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedbackId, token]);

    const loadSession = useCallback(async () => {
        try {
            const res = await fetch(
                `${API_BASE}/api/recovery/respond-now/${feedbackId}/session?token=${encodeURIComponent(token)}`
            );
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(stringifyDetail(data.detail) || `Error ${res.status}`);
            }
            const data = await res.json();
            setSession(data);
            setPhase(data.status === 'active' || data.status === 'inactive' ? 'active' : 'ended');
            setTimeout(scrollToBottom, 100);
        } catch (e) {
            setError(e.message);
            setPhase('error');
        }
    }, [feedbackId, token, scrollToBottom]);

    // Poll for new messages every 8 seconds
    useEffect(() => {
        if (phase !== 'active') return;
        pollRef.current = setInterval(loadSession, 8000);
        return () => clearInterval(pollRef.current);
    }, [phase, loadSession]);

    const sendMessage = async () => {
        const text = messageText.trim();
        if (!text || sending) return;

        setSending(true);
        try {
            const res = await fetch(
                `${API_BASE}/api/recovery/respond-now/${feedbackId}/message?token=${encodeURIComponent(token)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ text }),
                }
            );
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(stringifyDetail(data.detail) || 'Failed to send');
            }
            setMessageText('');
            await loadSession();
        } catch (e) {
            setError(e.message);
        } finally {
            setSending(false);
        }
    };

    const resolveSession = async () => {
        if (resolving) return;
        setResolving(true);
        try {
            const res = await fetch(
                `${API_BASE}/api/recovery/respond-now/${feedbackId}/resolve?token=${encodeURIComponent(token)}`,
                { method: 'PUT' }
            );
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(stringifyDetail(data.detail) || 'Failed to resolve');
            }
            setPhase('ended');
            setSession(prev => prev ? { ...prev, status: 'resolved' } : prev);
        } catch (e) {
            setError(e.message);
        } finally {
            setResolving(false);
        }
    };

    // — LOADING / CREATING STATE —
    if (phase === 'init' || phase === 'creating') {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="respond-now-loading">
                <div className="text-center space-y-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                        <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                    </div>
                    <div>
                        <p className="font-semibold text-slate-800">{t('respondNow.startingSession')}</p>
                        <p className="text-sm text-slate-500 mt-1">Sending secure link to customer...</p>
                    </div>
                </div>
            </div>
        );
    }

    // — ERROR STATE —
    if (phase === 'error') {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="respond-now-error">
                <div className="text-center space-y-4 max-w-sm">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                        <p className="font-semibold text-slate-800">{t('respondNow.unableToStart')}</p>
                        <p className="text-sm text-slate-500 mt-2">{error}</p>
                    </div>
                    <p className="text-xs text-slate-400">
                        You can still respond via the YulOnTap portal under Review Shield Results.
                    </p>
                </div>
            </div>
        );
    }

    // — ENDED STATE —
    if (phase === 'ended') {
        const statusCfg = STATUS_CONFIG[session?.status] || STATUS_CONFIG.resolved;
        const StatusIcon = statusCfg.icon;
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="respond-now-ended">
                <div className="text-center space-y-4 max-w-sm">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                        <StatusIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <p className="font-semibold text-slate-800">Session {statusCfg.label}</p>
                        <p className="text-sm text-slate-500 mt-1">
                            This conversation has been {session?.status || 'closed'}. Thank you for following up.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // — ACTIVE CONVERSATION STATE —
    const statusCfg = STATUS_CONFIG[session?.status] || STATUS_CONFIG.active;
    const messages = session?.messages || [];

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="respond-now-active">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10">
                <div className="max-w-lg mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-emerald-600" />
                        <span className="font-semibold text-slate-800 text-sm">{t('respondNow.directResponse')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full ${statusCfg.color}`} />
                            <span className="text-xs text-slate-500">{statusCfg.label}</span>
                        </div>
                        {session?.expires_at && (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTimeRemaining(session.expires_at)}
                            </span>
                        )}
                    </div>
                </div>
            </header>

            {/* Feedback context card */}
            <div className="max-w-lg mx-auto w-full px-4 pt-3">
                <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{t('respondNow.originalFeedback')}</span>
                        <div className="flex items-center gap-0.5">
                            {[1,2,3,4,5].map(i => (
                                <Star key={i} className={`w-3.5 h-3.5 ${i <= (session?.feedback_rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                            ))}
                        </div>
                    </div>
                    {session?.feedback_category && (
                        <span className="inline-block text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                            {session.feedback_category}
                        </span>
                    )}
                    {session?.feedback_message && (
                        <p className="text-sm text-slate-700 leading-relaxed">{session.feedback_message}</p>
                    )}
                    {session?.customer_email && (
                        <p className="text-xs text-slate-400">Customer: {session.customer_email}</p>
                    )}
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 max-w-lg mx-auto w-full space-y-3">
                {messages.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm">
                        <MessageSquareText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        <p>No messages yet. Type your first reply below.</p>
                        <p className="text-xs mt-1">The customer has received a secure link to respond.</p>
                    </div>
                )}
                {messages.map((msg) => {
                    const isManager = msg.sender === 'manager';
                    const isSystem = msg.sender === 'system';
                    if (isSystem) {
                        return (
                            <div key={msg.id} className="flex justify-center">
                                <div className="bg-slate-100 text-slate-500 text-xs px-3 py-1.5 rounded-full">
                                    {msg.text}
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div key={msg.id} className={`flex ${isManager ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                                isManager
                                    ? 'bg-emerald-600 text-white rounded-br-md'
                                    : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                            }`}>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                                <p className={`text-[10px] mt-1 ${isManager ? 'text-emerald-200' : 'text-slate-400'}`}>
                                    {formatTimestamp(msg.timestamp)}
                                </p>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="bg-white border-t border-slate-200 px-4 py-3 sticky bottom-0">
                <div className="max-w-lg mx-auto space-y-2">
                    <div className="flex gap-2">
                        <Textarea
                            data-testid="respond-now-input"
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            placeholder="Type your reply..."
                            className="flex-1 min-h-[44px] max-h-[120px] resize-none text-sm"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                        />
                        <Button
                            data-testid="respond-now-send"
                            onClick={sendMessage}
                            disabled={!messageText.trim() || sending}
                            size="icon"
                            className="bg-emerald-600 hover:bg-emerald-700 h-[44px] w-[44px] shrink-0"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            data-testid="respond-now-resolve"
                            variant="outline"
                            size="sm"
                            onClick={resolveSession}
                            disabled={resolving}
                            className="text-xs text-slate-500 hover:text-blue-600"
                        >
                            {resolving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                            Mark Resolved
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
